<template>
  <div
    class="text-semantic-surface-on-surface bg-semantic-surface-default py-10 md:py-20"
    :data-testid="dataTestid"
  >
    <div
      class="mx-auto"
      :class="maxWidth"
    >
      <Component
        :is="headerTag"
        v-if="$slots['header']"
        class="px-4 lg:px-0 text-2xl/[120%] md:text-4xl/[120%] text-center font-semibold"
        :class="[
          titleClass, {
            'mb-2 md:mb-4': $slots['sub-header']
          }
        ]"
      >
        <slot name="header" />
      </Component>

      <p
        v-if="$slots['sub-header'] && !hideSubHeader"
        class="md:text-lg/[150%] text-center px-4 lg:px-0 mb-6 md:mb-10"
        :class="subTitleClass"
      >
        <slot name="sub-header" />
      </p>

      <ClientOnly>
        <div class="overflow-x-auto overflow-y-hidden flex justify-center-safe px-3 md:px-0" :class="outerWrapperClass">
          <div 
            class="items-stretch flex pb-2 md:pb-0"
            :class="[
              {
                'md:mt-10 mt-6': !$slots['sub-header'] && !hideSubHeader,
                'md:grid gap-4 md:gap-x-8 md:gap-y-6 grid-cols-7 justify-center-safe': hideScrollBar,
                'flex gap-x-4 md:gap-x-8': !hideScrollBar
              }, 
              !hideScrollBar ? gapClass : '', 
              wrapperInlineClass
            ]"
          >
            <V2MoleculesCardImageCard
              v-for="company in companies"
              :key="company.company || company"
              :width="(company?.dimensions && company.dimensions.width)"
              :height="(company?.dimensions && company.dimensions.height)"
              :image="
                company.image ||
                  `${company.assetsBaseUrl || assetsBaseUrl}${
                    company.company || company
                  }`
              "
              class="shrink"
              :wrapper-class="itemCardClass"
              :class="itemClass"
              :card-width="cardWidth"
              :variant="cardVariant"
              :to="company?.url"
            />

            <V2MoleculesCardImageCard
              v-if="showMore"
              :class="itemClass"
              :card-width="cardWidth"
              :variant="cardVariant"
            >
              <V2AtomsButton
                :id="moreButtonTrackingParams?.elementId"
                size="small"
                theme="tertiary"
                class="!p-0"
                :to="moreButtonUrl"
                :enable-tracking="enableMoreButtonTracking"
                :tracking-additional-params="moreButtonTrackingParams"
              >
                + More
              </V2AtomsButton>
            </V2MoleculesCardImageCard>
          </div>
        </div>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup>
defineOptions({
  name: 'V2OrganismHomeCompanyComparison'
})

defineProps({
  dataTestid: {
    type: String,
    default: 'v2-home-company-comparison'
  },

  companies: {
    type: Array,
    required: true
  },

  moreButtonUrl: {
    type: String,
    default: null
  },

  showMore: {
    type: Boolean,
    default: true
  },

  assetsBaseUrl: {
    type: String,
    default: `${APP_ASSET_BASEURL}edge/static/images/company-logos/`
  },

  maxWidth: {
    type: String,
    default: 'max-w-wrapper-md'
  },

  titleClass: {
    type: String,
    default: ''
  },

  subTitleClass: {
    type: String,
    default: ''
  },

  hideSubHeader: {
    type: Boolean,
    default: false
  },

  gapClass: {
    type: String,
    default: 'gap-x-4 md:gap-x-8'
  },

  isCarousel: {
    type: Boolean,
    default: false
  },

  hideScrollBar: {
    type: Boolean,
    default: false
  },

  cardWidth: {
    type: [String, Number],
    default: ''
  },

  itemClass: {
    type: String,
    default: ''
  },

  itemCardClass: {
    type: String,
    default: ''
  },

  outerWrapperClass: {
    type: String,
    default: ''
  },

  wrapperInlineClass: {
    type: String,
    default: ''
  },

  cardVariant: {
    type: String,
    default: 'default',
    validator: value => ['default', 'transparent'].includes(value)
  },

  headerTag: {
    type: String,
    default: 'p',
    validator: value =>
      ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div'].includes(value)
  },

  enableMoreButtonTracking: {
    type: Boolean,
    default: false
  },

  moreButtonTrackingParams: {
    type: Object,
    default: () => ({})
  }
})
</script>
